import React from 'react';
import { Shield, Sparkles, Heart, Users, Video } from 'lucide-react';

const ColorTestRosaPlata = () => {
  // Amatista con MÁS ROSA que azul - variantes rosadas/magenta
  const amatistaRosadaOptions = [
    {
      name: 'Rosa Amatista',
      primary: '#7d5070',
      primaryLight: '#8d6080',
      accent: '#c9a227',
      description: 'Amatista muy rosada, femenina y cálida',
      vibe: 'Amor, corazón abierto, sostén maternal'
    },
    {
      name: 'Magenta Suave',
      primary: '#7a4a68',
      primaryLight: '#8a5a78',
      accent: '#c9a227',
      description: 'Magenta con profundidad, intenso pero suave',
      vibe: 'Transformación profunda, poder femenino'
    },
    {
      name: 'Rosa Orquídea',
      primary: '#8a5878',
      primaryLight: '#9a6888',
      accent: '#c9a227',
      description: 'Rosa orquídea, delicado y luminoso',
      vibe: 'Delicadeza, belleza interior, florecimiento'
    },
    {
      name: 'Fucsia Antiguo',
      primary: '#804868',
      primaryLight: '#905878',
      accent: '#c9a227',
      description: 'Fucsia envejecido, sofisticado',
      vibe: 'Pasión serena, intensidad controlada'
    },
    {
      name: 'Malva Rosado',
      primary: '#886078',
      primaryLight: '#987088',
      accent: '#c9a227',
      description: 'Malva con predominio rosa, muy cálido',
      vibe: 'Ternura, compasión, abrazo energético'
    },
    {
      name: 'Rosa Pétalo',
      primary: '#8f6080',
      primaryLight: '#9f7090',
      accent: '#c9a227',
      description: 'Como pétalos de rosa secados, nostálgico',
      vibe: 'Memoria del corazón, sanación emocional'
    },
    {
      name: 'Ciruela Rosa',
      primary: '#7a4860',
      primaryLight: '#8a5870',
      accent: '#c9a227',
      description: 'Ciruela con matiz rosa intenso',
      vibe: 'Profundidad, madurez, sabiduría del corazón'
    },
    {
      name: 'Rosa Místico',
      primary: '#855070',
      primaryLight: '#956080',
      accent: '#c9a227',
      description: 'Rosa con misterio, espiritual',
      vibe: 'Conexión sagrada, intuición del corazón'
    }
  ];

  // Plata MUY CLARO tirando a BLANCO - extremadamente luminoso
  const plataBlancoOptions = [
    {
      name: 'Plata Nieve',
      primary: '#d8dce0',
      primaryLight: '#e8ecf0',
      gradient: 'linear-gradient(135deg, #c8ccd0 0%, #e0e4e8 50%, #f0f2f5 100%)',
      accent: '#c9a227',
      textDark: '#5a5e62',
      description: 'Plata casi blanca como nieve',
      vibe: 'Pureza absoluta, claridad, ligereza'
    },
    {
      name: 'Blanco Platino',
      primary: '#e0e2e5',
      primaryLight: '#f0f2f5',
      gradient: 'linear-gradient(135deg, #d0d2d5 0%, #e8eaed 50%, #f8f9fa 100%)',
      accent: '#c9a227',
      textDark: '#606468',
      description: 'Blanco con brillo platino sutil',
      vibe: 'Luminosidad, elegancia etérea, pureza'
    },
    {
      name: 'Perla Blanca',
      primary: '#e5e3e0',
      primaryLight: '#f5f3f0',
      gradient: 'linear-gradient(135deg, #d5d3d0 0%, #eae8e5 50%, #faf8f5 100%)',
      accent: '#c9a227',
      textDark: '#5f5d5a',
      description: 'Blanco nacarado con calidez',
      vibe: 'Calidez sutil, luz interior, refinamiento'
    },
    {
      name: 'Niebla Blanca',
      primary: '#dde0e3',
      primaryLight: '#edf0f3',
      gradient: 'linear-gradient(135deg, #cdd0d3 0%, #e5e8eb 50%, #f5f8fa 100%)',
      accent: '#c9a227',
      textDark: '#585b5e',
      description: 'Niebla muy difuminada, casi blanca',
      vibe: 'Misterio suave, protección etérea'
    },
    {
      name: 'Cristal de Hielo',
      primary: '#e2e6ea',
      primaryLight: '#f2f6fa',
      gradient: 'linear-gradient(135deg, #d2d6da 0%, #e8ecf0 50%, #f8fafc 100%)',
      accent: '#c9a227',
      textDark: '#5c6064',
      description: 'Cristalino, muy frío y puro',
      vibe: 'Claridad mental total, límites nítidos'
    },
    {
      name: 'Luna Llena',
      primary: '#e0e4e8',
      primaryLight: '#f0f4f8',
      gradient: 'linear-gradient(135deg, #d0d4d8 0%, #e8ecf0 50%, #f8faff 100%)',
      accent: '#c9a227',
      textDark: '#5a5e65',
      description: 'Luz de luna llena, muy brillante',
      vibe: 'Iluminación, ciclos completos, plenitud'
    }
  ];

  return (
    <div className="min-h-screen bg-white pt-24 pb-20">
      <div className="max-w-7xl mx-auto px-6">
        <h1 className="text-4xl font-heading text-center mb-4">
          Nuevas Pruebas: Rosa Amatista & Plata Blanco
        </h1>
        <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
          <strong>Facilitadoras:</strong> Amatista con MÁS componente rosa/magenta<br/>
          <strong>Límites:</strong> Plata muy claro, casi blanco, difuminado
        </p>

        {/* FACILITADORAS - AMATISTA ROSADA */}
        <h2 className="text-2xl font-heading mb-8 pb-4 border-b-2 border-pink-200">
          🌸 FACILITADORAS — Amatista Rosa (más rosa que azul)
        </h2>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
          {amatistaRosadaOptions.map((palette, idx) => (
            <div key={idx} className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-heading text-lg">{palette.name}</h3>
                  <p className="text-sm text-gray-500">{palette.description}</p>
                  <p className="text-xs mt-1" style={{ color: palette.primary }}>✦ {palette.vibe}</p>
                </div>
                <div className="flex gap-2">
                  <div className="w-12 h-12 rounded-xl shadow" style={{ backgroundColor: palette.primary }} />
                  <div className="w-12 h-12 rounded-xl shadow" style={{ backgroundColor: palette.accent }} />
                </div>
              </div>

              <div 
                className="rounded-2xl overflow-hidden shadow-xl"
                style={{ background: `linear-gradient(135deg, ${palette.primary} 0%, ${palette.primaryLight} 100%)` }}
              >
                <div className="p-6">
                  <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-3 py-1.5 mb-4">
                    <Heart className="w-3 h-3" style={{ color: palette.accent }} />
                    <span className="text-xs text-white/90">Formación Especial · 3ª Edición</span>
                  </div>
                  <h4 className="text-2xl font-heading text-white mb-2">Facilitadoras en Sostén Energético</h4>
                  <p className="text-white/70 text-sm mb-4">Sostener es un arte vibracional que nace del amor...</p>
                  <div className="flex gap-3 items-center">
                    <button className="px-4 py-2 rounded-full text-xs font-medium text-white" style={{ backgroundColor: palette.accent }}>
                      Ver formación
                    </button>
                    <span className="text-xl font-heading" style={{ color: palette.accent }}>1.020€</span>
                  </div>
                </div>
              </div>
              
              {/* Preview en fondo claro */}
              <div className="p-4 bg-gray-50 rounded-xl">
                <div className="flex gap-3 items-center">
                  <button className="px-4 py-2 rounded-full text-xs font-medium text-white" style={{ backgroundColor: palette.primary }}>
                    Botón color
                  </button>
                  <button className="px-4 py-2 rounded-full text-xs font-medium text-white" style={{ backgroundColor: palette.accent }}>
                    Botón dorado
                  </button>
                  <span className="text-sm font-medium" style={{ color: palette.primary }}>Texto color</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* LÍMITES - PLATA BLANCO */}
        <h2 className="text-2xl font-heading mb-8 pb-4 border-b-2 border-gray-200">
          ❄️ LÍMITES — Plata Muy Claro (casi blanco)
        </h2>
        
        <div className="space-y-10">
          {plataBlancoOptions.map((palette, idx) => (
            <div key={idx}>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-heading text-lg">{palette.name}</h3>
                  <p className="text-sm text-gray-500">{palette.description}</p>
                  <p className="text-xs mt-1" style={{ color: palette.textDark }}>✦ {palette.vibe}</p>
                </div>
                <div className="flex gap-2">
                  <div className="w-12 h-12 rounded-xl shadow border border-gray-200" style={{ background: palette.gradient }} />
                  <div className="w-12 h-12 rounded-xl shadow" style={{ backgroundColor: palette.accent }} />
                </div>
              </div>

              <div 
                className="rounded-2xl overflow-hidden shadow-lg border border-gray-100"
                style={{ background: palette.gradient }}
              >
                <div className="p-8">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                    <div>
                      <div 
                        className="inline-flex items-center gap-2 rounded-full px-4 py-2 mb-4"
                        style={{ backgroundColor: 'rgba(255,255,255,0.6)', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}
                      >
                        <Shield className="w-4 h-4" style={{ color: palette.textDark }} />
                        <span className="text-sm" style={{ color: palette.textDark }}>Formación Especial · En vivo</span>
                      </div>
                      <h4 className="text-3xl font-heading mb-2" style={{ color: palette.textDark }}>LÍMITES</h4>
                      <p className="text-lg font-heading mb-4" style={{ color: palette.textDark, opacity: 0.7 }}>
                        La protección original de la consciencia
                      </p>
                      <p className="mb-6" style={{ color: palette.textDark, opacity: 0.8 }}>
                        Un master-curso encarnado para recuperar tu eje sin culpa, sin dureza y sin perder el corazón.
                      </p>
                      <div className="flex flex-wrap gap-4 mb-6">
                        <div className="flex items-center gap-2" style={{ color: palette.textDark, opacity: 0.7 }}>
                          <Video className="w-4 h-4" />
                          <span className="text-sm">8 sesiones en directo</span>
                        </div>
                        <div className="flex items-center gap-2" style={{ color: palette.textDark, opacity: 0.7 }}>
                          <Users className="w-4 h-4" />
                          <span className="text-sm">14 plazas</span>
                        </div>
                      </div>
                      <div className="flex gap-3 items-center">
                        <button 
                          className="px-6 py-3 rounded-full text-sm font-medium text-white shadow-lg"
                          style={{ backgroundColor: palette.accent }}
                        >
                          Solicitar acceso
                        </button>
                        <button 
                          className="px-6 py-3 rounded-full text-sm font-medium border-2"
                          style={{ borderColor: palette.textDark, color: palette.textDark }}
                        >
                          Ver detalles
                        </button>
                        <span className="text-2xl font-heading ml-2" style={{ color: palette.accent }}>420€</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-center">
                      <div 
                        className="text-center p-8 rounded-2xl"
                        style={{ backgroundColor: 'rgba(255,255,255,0.5)', boxShadow: '0 4px 20px rgba(0,0,0,0.03)' }}
                      >
                        <Shield className="w-16 h-16 mx-auto mb-4" style={{ color: palette.textDark, opacity: 0.25 }} strokeWidth={1} />
                        <p className="font-heading italic" style={{ color: palette.textDark, opacity: 0.6 }}>
                          "El límite no separa.<br/>Protege la vida."
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Light section preview */}
              <div className="mt-4 p-4 bg-white border border-gray-100 rounded-xl flex gap-3 items-center shadow-sm">
                <button 
                  className="px-4 py-2 rounded-full text-xs font-medium text-white"
                  style={{ backgroundColor: palette.accent }}
                >
                  Botón dorado
                </button>
                <button 
                  className="px-4 py-2 rounded-full text-xs font-medium border"
                  style={{ borderColor: palette.textDark, color: palette.textDark }}
                >
                  Botón outline
                </button>
                <span className="text-sm" style={{ color: palette.textDark }}>
                  Texto sobre fondo blanco
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Mis recomendaciones */}
        <div className="mt-16 p-8 bg-gradient-to-br from-pink-50 to-gray-50 rounded-3xl">
          <h2 className="text-2xl font-heading mb-6 text-center">🌟 Mis Favoritas</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white p-6 rounded-2xl shadow-sm">
              <h3 className="font-heading text-lg mb-4" style={{ color: '#7d5070' }}>Para FACILITADORAS (Rosa)</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full shadow" style={{ backgroundColor: '#7d5070' }} />
                  <div>
                    <p className="font-medium">🥇 Rosa Amatista</p>
                    <p className="text-xs text-gray-500">Muy femenina, cálida, corazón abierto</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full shadow" style={{ backgroundColor: '#886078' }} />
                  <div>
                    <p className="font-medium">🥈 Malva Rosado</p>
                    <p className="text-xs text-gray-500">Ternura, compasión, abrazo energético</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full shadow" style={{ backgroundColor: '#855070' }} />
                  <div>
                    <p className="font-medium">🥉 Rosa Místico</p>
                    <p className="text-xs text-gray-500">Espiritual pero cercano al corazón</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white p-6 rounded-2xl shadow-sm">
              <h3 className="font-heading text-lg mb-4 text-gray-600">Para LÍMITES (Plata Blanco)</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full shadow border border-gray-200" style={{ background: 'linear-gradient(135deg, #d0d2d5, #f8f9fa)' }} />
                  <div>
                    <p className="font-medium">🥇 Blanco Platino</p>
                    <p className="text-xs text-gray-500">Luminosidad máxima, elegancia etérea</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full shadow border border-gray-200" style={{ background: 'linear-gradient(135deg, #c8ccd0, #f0f2f5)' }} />
                  <div>
                    <p className="font-medium">🥈 Plata Nieve</p>
                    <p className="text-xs text-gray-500">Pureza absoluta, muy claro</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full shadow border border-gray-200" style={{ background: 'linear-gradient(135deg, #d5d3d0, #faf8f5)' }} />
                  <div>
                    <p className="font-medium">🥉 Perla Blanca</p>
                    <p className="text-xs text-gray-500">Blanco cálido nacarado, sutil</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Códigos de colores */}
        <div className="mt-8 p-6 bg-gray-900 rounded-2xl text-white">
          <h3 className="font-heading mb-4">📋 Códigos de Colores</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs font-mono">
            <div><span className="text-pink-400">Rosa Amatista:</span> #7d5070</div>
            <div><span className="text-pink-400">Magenta Suave:</span> #7a4a68</div>
            <div><span className="text-pink-400">Rosa Orquídea:</span> #8a5878</div>
            <div><span className="text-pink-400">Malva Rosado:</span> #886078</div>
            <div><span className="text-gray-400">Plata Nieve:</span> #d8dce0</div>
            <div><span className="text-gray-400">Blanco Platino:</span> #e0e2e5</div>
            <div><span className="text-gray-400">Perla Blanca:</span> #e5e3e0</div>
            <div><span className="text-gray-400">Luna Llena:</span> #e0e4e8</div>
          </div>
        </div>

        {/* Instrucciones */}
        <div className="mt-8 p-6 bg-pink-50 rounded-2xl">
          <h3 className="font-heading mb-2">💡 Para decidir:</h3>
          <p className="text-sm text-gray-700">
            Observa cada opción y dime:<br/>
            <strong>"Quiero [nombre del rosa] para Facilitadoras y [nombre del plata] para Límites"</strong><br/>
            Por ejemplo: "Quiero Rosa Amatista para Facilitadoras y Blanco Platino para Límites"
          </p>
        </div>
      </div>
    </div>
  );
};

export default ColorTestRosaPlata;
